import { Check, Copy, ExternalLink, QrCode, RadioTower } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type TagProvision } from "../api";

export function NfcLinkPanel({
  unitId,
  onChanged
}: {
  unitId: string;
  onChanged: () => void;
}) {
  const [tags, setTags] = useState<TagProvision[]>([]);
  const [working, setWorking] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(() => {
    api
      .getInventoryNfc(unitId)
      .then(setTags)
      .catch((reason) =>
        setError(reason instanceof Error ? reason.message : "Could not load NFC links")
      );
  }, [unitId]);

  useEffect(() => {
    load();
  }, [load]);

  const active = tags.find((tag) => ["active", "assigned"].includes(tag.status));

  async function generate() {
    setWorking(true);
    setError("");
    try {
      await api.createInventoryNfc(unitId);
      load();
      onChanged();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not generate NFC link");
    } finally {
      setWorking(false);
    }
  }

  async function copyUrl() {
    if (!active?.url) return;
    await navigator.clipboard.writeText(active.url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <section className="nfc-link-panel">
      <header>
        <RadioTower />
        <div>
          <h2>NFC scan link</h2>
          <p>
            Write this unique URL to one NFC tag. Anyone on this private network can scan it and
            read this inventory record.
          </p>
        </div>
      </header>

      {active?.url && active.token ? (
        <div className="nfc-link-ready">
          <img src={`/api/tags/${encodeURIComponent(active.token)}/qr.png`} alt="NFC link QR code" />
          <div>
            <label>
              Unique NFC URL
              <input value={active.url} readOnly onFocus={(event) => event.currentTarget.select()} />
            </label>
            <small>Tag ID: {active.token_preview}</small>
            <div className="nfc-link-actions">
              <button className="button primary" onClick={copyUrl}>
                {copied ? <Check /> : <Copy />}
                {copied ? "Copied" : "Copy link"}
              </button>
              <a className="button secondary" href={active.url} target="_blank" rel="noreferrer">
                <ExternalLink />
                Test link
              </a>
              <a
                className="button secondary"
                href={`/api/tags/${encodeURIComponent(active.token)}/label.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                <QrCode />
                Print QR label
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className="nfc-link-empty">
          <p>This inventory unit does not have an active NFC link yet.</p>
          <button className="button primary" onClick={generate} disabled={working}>
            <RadioTower />
            {working ? "Generating…" : "Generate unique NFC link"}
          </button>
        </div>
      )}

      {active && (
        <details className="tag-management">
          <summary>Tag management</summary>
          <div>
            {tags.map((tag) => (
              <div className="tag-management-row" key={tag.id}>
                <span>
                  <strong>{tag.token_preview}</strong>
                  <small>{tag.status}</small>
                </span>
                {!["retired", "replaced"].includes(tag.status) && (
                  <button
                    className="button danger-button"
                    onClick={async () => {
                      if (!window.confirm(`Retire tag ${tag.token_preview}?`)) return;
                      await api.updateTagStatus(tag.id, "retired", "Retired from inventory record");
                      load();
                      onChanged();
                    }}
                  >
                    Retire tag
                  </button>
                )}
              </div>
            ))}
            <Link className="button secondary" to="/provision">
              Provision replacement tag
            </Link>
          </div>
        </details>
      )}
      {error && <p className="form-error">{error}</p>}
    </section>
  );
}
